import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaClock, FaUndo } from 'react-icons/fa';
import Swal from 'sweetalert2';
import conciliacionBancariaService from '../../services/conciliacionBancariaService';
import '../../styles/pages/ConciliacionBancaria.css';

const MESES = [
  { value: '0',  label: 'Todos los meses' },
  { value: '1',  label: 'Enero' },
  { value: '2',  label: 'Febrero' },
  { value: '3',  label: 'Marzo' },
  { value: '4',  label: 'Abril' },
  { value: '5',  label: 'Mayo' },
  { value: '6',  label: 'Junio' },
  { value: '7',  label: 'Julio' },
  { value: '8',  label: 'Agosto' },
  { value: '9',  label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const TIPOS_CONCILIACION = [
  { value: 'venta',      label: 'Venta / Ingreso' },
  { value: 'compra',     label: 'Compra / Proveedor' },
  { value: 'gasto',      label: 'Gasto' },
  { value: 'interno',    label: 'Transferencia interna' },
  { value: 'otro',       label: 'Otro' },
];

const ANIO_ACTUAL = new Date().getFullYear();
const ANIOS = [ANIO_ACTUAL - 2, ANIO_ACTUAL - 1, ANIO_ACTUAL, ANIO_ACTUAL + 1];

const fmt = (n) =>
  n ? Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }) : '-';

const fmtFecha = (d) =>
  d ? new Date(d).toLocaleDateString('es-CL') : '-';

const TIPO_LABEL = Object.fromEntries(TIPOS_CONCILIACION.map(t => [t.value, t.label]));

const ESTADO_INICIAL_TOTALES = { total: 0, pendientes: 0, conciliados: 0, total_cargos: 0, total_abonos: 0 };

const CONFIANZA_COLOR = (n) => {
  if (n >= 85) return '#276749';
  if (n >= 65) return '#b7791f';
  return '#718096';
};

// ── Modal de conciliación ────────────────────────────────────────────────────
const ConciliarModal = ({ movimiento, sugerencias, loadingSug, onConfirm, onCancel, saving }) => {
  const [tipo, setTipo] = useState('');
  const [nota, setNota] = useState('');

  useEffect(() => {
    if (!movimiento) return;
    setTipo('');
    setNota('');
  }, [movimiento]);

  if (!movimiento) return null;

  const esCargo = Number(movimiento.cargo) > 0;

  const aplicarSugerencia = (s) => {
    setTipo(s.tipo_sugerido);
    setNota(s.nota_sugerida);
  };

  return (
    <div className="cb-modal-overlay" onClick={onCancel}>
      <div className="cb-modal cb-modal--wide" onClick={e => e.stopPropagation()}>
        <h3 className="cb-modal-title">Conciliar movimiento</h3>

        {/* Resumen del movimiento */}
        <div className="cb-modal-resumen">
          <div><span>Fecha</span><strong>{fmtFecha(movimiento.fecha)}</strong></div>
          <div><span>Banco</span><strong>{movimiento.nombre_cuenta || movimiento.banco}</strong></div>
          <div><span>Descripción</span><strong>{movimiento.descripcion}</strong></div>
          <div>
            <span>{esCargo ? 'Cargo' : 'Abono'}</span>
            <strong className={esCargo ? 'cb-monto-cargo' : 'cb-monto-abono'}>
              {esCargo ? fmt(movimiento.cargo) : fmt(movimiento.abono)}
            </strong>
          </div>
        </div>

        {/* Sugerencias automáticas */}
        <div className="cb-modal-field">
          <label>Sugerencias automáticas</label>
          {loadingSug ? (
            <p className="cb-sug-loading">Analizando...</p>
          ) : sugerencias.length === 0 ? (
            <p className="cb-sug-empty">Sin sugerencias para este movimiento</p>
          ) : (
            <div className="cb-sug-lista">
              {sugerencias.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className={`cb-sug-card ${tipo === s.tipo_sugerido && nota === s.nota_sugerida ? 'cb-sug-card--activa' : ''}`}
                  onClick={() => aplicarSugerencia(s)}
                >
                  <span className="cb-sug-confianza" style={{ color: CONFIANZA_COLOR(s.confianza) }}>
                    {s.confianza}%
                  </span>
                  <span className="cb-sug-titulo">{s.titulo}</span>
                  <span className="cb-sug-desc">{s.descripcion}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tipo manual */}
        <div className="cb-modal-field">
          <label>Tipo de movimiento</label>
          <div className="cb-tipo-grid">
            {TIPOS_CONCILIACION.map(t => (
              <button
                key={t.value}
                className={`cb-tipo-btn ${tipo === t.value ? 'cb-tipo-btn--active' : ''}`}
                onClick={() => setTipo(t.value)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cb-modal-field">
          <label>Nota <span className="cb-opcional">(opcional)</span></label>
          <textarea
            className="cb-modal-textarea"
            placeholder="Ej: Pago cliente Juan Pérez, factura #1234..."
            value={nota}
            onChange={e => setNota(e.target.value)}
            rows={2}
          />
        </div>

        <div className="cb-modal-actions">
          <button className="cb-btn-cancelar" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button
            className="cb-btn-confirmar"
            onClick={() => onConfirm(tipo, nota)}
            disabled={!tipo || saving}
          >
            {saving ? 'Guardando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────
const ConciliarPage = ({ token, id_company }) => {
  const navigate = useNavigate();

  // Filtros
  const [anio,       setAnio]       = useState(ANIO_ACTUAL);
  const [mes,        setMes]        = useState(String(new Date().getMonth() + 1));
  const [idCuenta,   setIdCuenta]   = useState('');
  const [tipoMonto,  setTipoMonto]  = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('pendiente');

  // Datos
  const [movimientos, setMovimientos] = useState([]);
  const [totales,     setTotales]     = useState(ESTADO_INICIAL_TOTALES);
  const [cuentas,     setCuentas]     = useState([]);
  const [bancos,      setBancos]      = useState([]);
  const [loading,     setLoading]     = useState(false);

  // Modal
  const [modalMov,    setModalMov]    = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [loadingSug,  setLoadingSug]  = useState(false);
  const [saving,      setSaving]      = useState(false);

  // ── Cargar cuentas una sola vez ─────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      conciliacionBancariaService.getCuentas(id_company, token),
      conciliacionBancariaService.getBancos(token),
    ]).then(([c, b]) => {
      setCuentas(c.filter(x => x.activo));
      setBancos(b);
    }).catch(() => {});
  }, [id_company, token]);

  // ── Cargar movimientos al cambiar filtros ───────────────────────────────
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const result = await conciliacionBancariaService.getMovimientosTodos({
        id_company,
        anio,
        mes,
        id_cuenta_banco: idCuenta || undefined,
        tipo_monto:      tipoMonto || undefined,
        estado:          estadoFiltro,
      }, token);
      setMovimientos(result.data);
      setTotales(result.totales);
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los movimientos' });
    } finally {
      setLoading(false);
    }
  }, [id_company, anio, mes, idCuenta, tipoMonto, estadoFiltro, token]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Abrir modal y cargar sugerencias ────────────────────────────────────
  const abrirModal = async (mov) => {
    setModalMov(mov);
    setSugerencias([]);
    setLoadingSug(true);
    try {
      const data = await conciliacionBancariaService.getSugerencias(mov.id, token);
      setSugerencias(data || []);
    } catch {
      setSugerencias([]);
    } finally {
      setLoadingSug(false);
    }
  };

  const cerrarModal = () => {
    setModalMov(null);
    setSugerencias([]);
    setLoadingSug(false);
  };

  // ── Conciliar ───────────────────────────────────────────────────────────
  const handleConfirmarConciliacion = async (tipo, nota) => {
    setSaving(true);
    try {
      await conciliacionBancariaService.conciliarMovimiento(
        modalMov.id,
        { tipo_conciliacion: tipo, nota_conciliacion: nota },
        token
      );
      cerrarModal();
      cargar();
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo conciliar el movimiento' });
    } finally {
      setSaving(false);
    }
  };

  // ── Revertir ────────────────────────────────────────────────────────────
  const handleRevertir = async (mov) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: '¿Revertir conciliación?',
      text: `El movimiento volverá a estado pendiente.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, revertir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#e53e3e',
    });
    if (!ok.isConfirmed) return;

    try {
      await conciliacionBancariaService.revertirPendiente(mov.id, token);
      cargar();
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo revertir el movimiento' });
    }
  };

  const getBancoLabel = (banco) => {
    const b = bancos.find(x => x.value === banco);
    return b ? b.label : banco;
  };

  return (
    <div className="cb-conciliar">
      <ConciliarModal
        movimiento={modalMov}
        sugerencias={sugerencias}
        loadingSug={loadingSug}
        onConfirm={handleConfirmarConciliacion}
        onCancel={cerrarModal}
        saving={saving}
      />

      <button className="cb-back-btn" onClick={() => navigate('/conciliacion-bancaria')}>
        <FaArrowLeft /> Volver
      </button>

      <h1>Conciliación Bancaria</h1>

      {/* ── Filtros ── */}
      <div className="cb-filtros">
        <div className="cb-filtro-grupo">
          <label>Año</label>
          <select value={anio} onChange={e => setAnio(Number(e.target.value))}>
            {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="cb-filtro-grupo">
          <label>Mes</label>
          <select value={mes} onChange={e => setMes(e.target.value)}>
            {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        <div className="cb-filtro-grupo">
          <label>Cuenta</label>
          <select value={idCuenta} onChange={e => setIdCuenta(e.target.value)}>
            <option value="">Todas las cuentas</option>
            {cuentas.map(c => (
              <option key={c.id} value={c.id}>
                {getBancoLabel(c.banco)}{c.nombre_cuenta ? ` · ${c.nombre_cuenta}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="cb-filtro-grupo">
          <label>Tipo</label>
          <select value={tipoMonto} onChange={e => setTipoMonto(e.target.value)}>
            <option value="">Todos</option>
            <option value="abonos">Solo abonos</option>
            <option value="cargos">Solo cargos</option>
          </select>
        </div>

        <div className="cb-filtro-grupo">
          <label>Estado</label>
          <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="conciliado">Conciliados</option>
          </select>
        </div>
      </div>

      {/* ── Resumen ── */}
      <div className="cb-resumen-cards">
        <div className="cb-resumen-card">
          <span className="cb-resumen-label">Movimientos</span>
          <span className="cb-resumen-valor">{totales.total}</span>
        </div>
        <div className="cb-resumen-card cb-resumen-card--pendiente">
          <span className="cb-resumen-label">Pendientes</span>
          <span className="cb-resumen-valor">{totales.pendientes}</span>
        </div>
        <div className="cb-resumen-card cb-resumen-card--conciliado">
          <span className="cb-resumen-label">Conciliados</span>
          <span className="cb-resumen-valor">{totales.conciliados}</span>
        </div>
        <div className="cb-resumen-card cb-resumen-card--cargo">
          <span className="cb-resumen-label">Total Cargos</span>
          <span className="cb-resumen-valor">{fmt(totales.total_cargos)}</span>
        </div>
        <div className="cb-resumen-card cb-resumen-card--abono">
          <span className="cb-resumen-label">Total Abonos</span>
          <span className="cb-resumen-valor">{fmt(totales.total_abonos)}</span>
        </div>
      </div>

      {/* ── Tabla ── */}
      {loading ? (
        <div className="cb-loading">Cargando movimientos...</div>
      ) : movimientos.length === 0 ? (
        <div className="cb-empty">No hay movimientos con los filtros seleccionados</div>
      ) : (
        <div className="cb-tabla-wrapper">
          <table className="cb-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Banco / Cuenta</th>
                <th>Descripción</th>
                <th className="cb-th-monto">Cargo</th>
                <th className="cb-th-monto">Abono</th>
                <th className="cb-th-monto">Saldo</th>
                <th>Estado</th>
                <th>Tipo</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map(mov => (
                <tr key={mov.id} className={mov.estado === 'conciliado' ? 'cb-row--conciliado' : ''}>
                  <td className="cb-td-fecha">{fmtFecha(mov.fecha)}</td>
                  <td className="cb-td-banco">
                    <span className="cb-banco-label">{getBancoLabel(mov.banco)}</span>
                    {mov.nombre_cuenta && <span className="cb-cuenta-label">{mov.nombre_cuenta}</span>}
                  </td>
                  <td className="cb-td-desc" title={mov.descripcion}>{mov.descripcion}</td>
                  <td className="cb-td-monto cb-monto-cargo">
                    {Number(mov.cargo) > 0 ? fmt(mov.cargo) : ''}
                  </td>
                  <td className="cb-td-monto cb-monto-abono">
                    {Number(mov.abono) > 0 ? fmt(mov.abono) : ''}
                  </td>
                  <td className="cb-td-monto">{fmt(mov.saldo)}</td>
                  <td>
                    {mov.estado === 'conciliado' ? (
                      <span className="cb-badge cb-badge--conciliado">
                        <FaCheckCircle /> Conciliado
                      </span>
                    ) : (
                      <span className="cb-badge cb-badge--pendiente">
                        <FaClock /> Pendiente
                      </span>
                    )}
                  </td>
                  <td className="cb-td-tipo">
                    {mov.tipo_conciliacion ? (
                      <span className="cb-tipo-tag">{TIPO_LABEL[mov.tipo_conciliacion] || mov.tipo_conciliacion}</span>
                    ) : '-'}
                    {mov.nota_conciliacion && (
                      <span className="cb-nota" title={mov.nota_conciliacion}>📝</span>
                    )}
                  </td>
                  <td>
                    {mov.estado === 'pendiente' ? (
                      <button className="cb-btn-conciliar" onClick={() => abrirModal(mov)}>
                        Conciliar
                      </button>
                    ) : (
                      <button className="cb-btn-revertir" onClick={() => handleRevertir(mov)}>
                        <FaUndo /> Revertir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ConciliarPage;
